import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchRepairItemDetail } from '#/api/aftersalesMgmt/repairItem/fetchDetail';
import { updateRepairItem } from '#/api/aftersalesMgmt/repairItem/update';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Textarea } from '#/modules/commonComponents/form/Textarea';
import RepairItemStatusBadge from '#/modules/crmMgmt/aftersales/repairItem/components/RepairItemStatusBadge';
import {
  getRepairItemApiErrorMessage,
  isRepairItemRefetchError,
} from '#/modules/crmMgmt/aftersales/repairItem/helper/apiError';
import { getRepairItemDocumentLinks } from '#/modules/crmMgmt/aftersales/repairItem/helper/repairItemLinks';
import PageShell from '#/modules/infra/layout/PageShell';
import { formatDateOrDash } from '#/utils/date';
import {
  repairItemDetailQueryKey,
  repairItemListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function DetailField({ label, children }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <div className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">{children}</div>
    </div>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function LinkedValue({ to, children, emptyLabel = null }) {
  if (!to) {
    return emptyLabel ?? '—';
  }
  return (
    <Link to={to} className="text-primary-600 underline">
      {children}
    </Link>
  );
}

LinkedValue.propTypes = {
  to: PropTypes.string,
  children: PropTypes.node,
  emptyLabel: PropTypes.node,
};

const RepairItemDetailPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [remarkDraft, setRemarkDraft] = useState(null);

  const detailQuery = useQuery({
    queryKey: repairItemDetailQueryKey(id),
    queryFn: () => fetchRepairItemDetail(id),
    enabled: Boolean(id),
    retry: false,
  });

  const detail = detailQuery.data?.data;
  const remarkLocked = Boolean(detail?.signReceiptId);
  const serverRemark = detail?.remark ?? '';
  const remarkSource = `${detail?.record?.updatedAt ?? ''}|${serverRemark}`;
  const remark = remarkDraft?.source === remarkSource ? remarkDraft.value : serverRemark;

  const updateMutation = useMutation({
    mutationFn: (payload) => updateRepairItem(id, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(repairItemDetailQueryKey(id), result);
      queryClient.invalidateQueries({ queryKey: repairItemListQueryKeyPrefix() });
      toast.success('已更新備註');
    },
    onError: (error) => {
      toast.error(getRepairItemApiErrorMessage(error, '更新備註失敗'));
      if (isRepairItemRefetchError(error)) {
        detailQuery.refetch();
      }
    },
  });

  const handleRemarkChange = useCallback((event) => {
    setRemarkDraft({ source: remarkSource, value: event.target.value });
  }, [remarkSource]);

  const handleSave = useCallback(() => {
    if (!detail?.id || remarkLocked || updateMutation.isPending) {
      return;
    }
    updateMutation.mutate({
      remark,
      updatedAt: detail.record?.updatedAt ?? detail.updatedAt,
    });
  }, [detail, remark, remarkLocked, updateMutation]);

  const links = getRepairItemDocumentLinks(detail);

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">維修品項詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.repairItem}>返回列表</Link>
            </Button>
          </div>

          {detailQuery.isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getRepairItemApiErrorMessage(detailQuery.error, '載入維修品項失敗')}
            </p>
          ) : null}

          {detail ? (
            <div className="receipt-card-elevate">
              <div className="receipt-card-surface p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {displayOrDash(detail.serialNo)}
                  </h2>
                  {detail.status ? (
                    <RepairItemStatusBadge status={detail.status} />
                  ) : null}
                </div>
                <div
                  className={cn(
                    'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
                    'sm:grid-cols-2',
                  )}
                >
                  <DetailField label="報修單號">
                    <LinkedValue to={links.repairRequestTo}>
                      {displayOrDash(detail.repairRequestNo)}
                    </LinkedValue>
                  </DetailField>
                  <DetailField label="報修日期">
                    {formatDateOrDash(detail.repairRequestDate)}
                  </DetailField>
                  <DetailField label="序號">{displayOrDash(detail.serialNo)}</DetailField>
                  <DetailField label="產品代號">{displayOrDash(detail.productCode)}</DetailField>
                  <DetailField label="品名">{displayOrDash(detail.itemName)}</DetailField>
                  <DetailField label="序號登錄">{displayOrDash(detail.mtrSnId)}</DetailField>
                  <DetailField label="問題">{displayOrDash(detail.problemDescription)}</DetailField>
                  <DetailField label="配件">{displayOrDash(detail.accessories)}</DetailField>
                  <DetailField label="維修報告">
                    <LinkedValue
                      to={links.repairReportTo}
                      emptyLabel={links.repairReportEmptyLabel}
                    >
                      維修報告
                    </LinkedValue>
                  </DetailField>
                  <DetailField label="簽收單">
                    <LinkedValue to={links.signReceiptTo}>簽收單</LinkedValue>
                  </DetailField>
                </div>
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <label htmlFor="repair-item-remark" className="text-xs text-gray-500">
                    備註
                  </label>
                  {remarkLocked ? (
                    <p className="mt-1 text-sm text-gray-600">已簽收，備註不可修改</p>
                  ) : null}
                  <Textarea
                    id="repair-item-remark"
                    className="textarea mt-1 min-h-25"
                    rows={3}
                    value={remark}
                    readOnly={remarkLocked}
                    onChange={handleRemarkChange}
                  />
                  {remarkLocked ? null : (
                    <Button
                      type="button"
                      className="mt-3"
                      disabled={updateMutation.isPending}
                      onClick={handleSave}
                    >
                      儲存備註
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default RepairItemDetailPage;
